import { reflectKeys } from "@utils/key.reflactor";
import { RecurringInstanceOption } from "@modules/calendar/calendar-types.ts";

export const NAMESPACE = {
  AUTH: "auth",
};

export const CALENDAR_VIEWS = {
  MONTH: "dayGridMonth",
  WEEK: "timeGridWeek",
  DAY: "timeGridDay",
} as {
  MONTH: string;
  WEEK: string;
  DAY: string;
};

export const CALENDAR_CUTOFF_DATE = {
  START_DATE: 1,
  START_MONTH: 0, // month index
  END_DATE: 31,
  END_MONTH: 11, // month index
};

export const CALENDAR_NO_OF_DAYS_IN_WEEK = 7;

export const CALENDAR_INIT_VIEW_LOCAL_STORAGE_KEY = "calendar-view-init-";
export const MAX_CALENDARS_SELECTED = 13;
export const MAX_PINNED_CALENDARS = MAX_CALENDARS_SELECTED - 1;
export const CALENDAR_TYPES = {
  PERSONAL: "PERSONAL",
  PINNED: "PINNED",
  STUDENT_GROUP: "STUDENT_GROUP",
  PERSONAL_TASK: "PERSONAL_TASK",
};

export const CALENDAR_EVENT_TYPES = {
  EVENT: "EVENT",
  TASK: "TASK",
};

export const CALENDAR_TYPES_ARRAY = ["PINNED", "STUDENT_GROUP", "COMMUNITY"];

export const CALENDER_STATUS = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
};

export const TIME_GRID_DAY_HEADER_FORMAT = "ddd/DD";
export const TIME_GRID_DAY_TITLE_FORMAT = "DD MMM YYYY";
export const TIME_GRID_WEEK_TITLE_FORMAT = "DD MMM";
export const DAY_GRID_DAY_HEADER_FORMAT = "ddd";
export const DAY_GRID_MONTH_TITLE_FORMAT = "MMMM YYYY";

export const SLOT_DURATION = "00:30";
export const SLOT_LABEL_INTERVAL = "01:00";
export const SLOT_LABEL_TIME_FORMAT = "hA";
export const SLOT_MIN_TIME = "00:00";
export const SLOT_MAX_TIME = "24:00";
export const SCROLL_TIME = "00:00";
export const SCROLL_TIME_ADJUSTMENT = { hours: -1, minutes: 0 };
export const EVENT_END_TIME_ADJUSTMENT = { hours: 0, minutes: 30 };
export const EVENT_TIME_FORMAT = "hh:mm A";
export const EVENT_TIME_SHORT = { hours: 0, minutes: 29 }; // short: <= 29 minutes
export const EVENT_TIME_LONG = { hours: 0, minutes: 41 }; // long: >= 41 minutes
export const MIN_EVENT_TIME_TO_DISPLAY = { hours: 0, minutes: 15 };
export const DEFAULT_EVENT_TIME = "00:00:00";
export const MAX_EVENT_TIME = "23:59:59";
export const DEFAULT_TITLE = "Untitled";
export const DEFAULT_EVENT_MIN_HEIGHT = 15;
export const DEFAULT_MAX_ATTACHMENTS = 20;

export const CLASS_GROUP_CALENDAR_TAB = "planner";

export const CALENDAR_EVENT_OBJECT = {
  TITLE: "title",
  ALL_DAY: "allDay",
  START: "start",
  END: "end",
  START_DATETIME: "startDateTime",
  CALENDAR: "calendar",
  EXTENDED_PROPS: "extendedProps",
  TASKS: "tasks",
  UUID: "uuid",
  SYSTEM_EVENT: "isSystemEvent",
};

export const DEFAULT_EVENT_ORDER = [
  CALENDAR_EVENT_OBJECT.START,
  "-duration",
  CALENDAR_EVENT_OBJECT.ALL_DAY,
  CALENDAR_EVENT_OBJECT.TITLE,
];

export const ALLDAY_EVENT_END_TIME_ADJUSTMENT = {
  NEXT_DAY_SOD: { seconds: 1 },
  PREV_DAY_EOD: { seconds: -1 },
};

export const CALENDAR_EVENT_TASK_LISTING_DUE = {
  d1: { days: 1 },
  d4: { days: 4 },
};

export const CALENDAR_ATTACHMENT_TYPE = {
  ASSIGNMENT: "ASSIGNMENT",
  LESSON: "LESSON",
  CM_NODE: "CM_NODE",
  LO: "LO",
  PENDING_ACTION: "PENDING_ACTION", //deprecated
  SELF_STUDY: "SELF_STUDY", //deprecated
};

export const PENDING_ACTION_TYPE = {
  ASSIGNMENT: "ASSIGNMENT",
  CLASS_GROUP: "CLASS_GROUP",
  PERSONAL: "PERSONAL",
  MOE: "MOE",
  APP: "APP",
};

export const CALENDAR_EVENT_DEFAULT_COLOR = {
  EVENT: "#B7BBBF", // Default color for personal/unclassified events
  SYSTEM: "#FFFBF0", // Prefilled system event for holiday etc
};

export const RECURRING_TYPE = {
  NONE: { value: "NONE", label: "calendar_recurring_type_none_label" },
  DAILY: { value: "DAILY", label: "calendar_recurring_type_daily_label" },
  WEEKLY: { value: "WEEKLY", label: "calendar_recurring_type_weekly_label" },
  MONTHLY: { value: "MONTHLY", label: "calendar_recurring_type_monthly_label" },
  CUSTOM: { value: "CUSTOM", label: "calendar_recurring_type_custom_label" },
};

export const RECURRING_FREQUECY = [
  { label: "calendar_custom_recurring_day_label", value: RECURRING_TYPE.DAILY.value },
  { label: "calendar_custom_recurring_week_label", value: RECURRING_TYPE.WEEKLY.value },
  { label: "calendar_custom_recurring_month_label", value: RECURRING_TYPE.MONTHLY.value },
];

export const RECURRING_FREQUENCY_BY_TYPE = {
  DAILY: 1,
  WEEKLY: 7,
  MONTHLY: 31,
};

export const RECURRING_INSTANCE_SELECTION = reflectKeys(["SINGLE", "SINGLE_AND_FUTURE", "ALL"]) as {
  SINGLE: string;
  SINGLE_AND_FUTURE: string;
  ALL: string;
};

export const RECURRING_INSTANCE_SELECTION_MODE = reflectKeys(["EDIT", "DELETE"]);

export const RECURRING_INSTANCE_EDIT: RecurringInstanceOption[] = [
  {
    label: "calendar_event_recurring_instance_selection_modal_edit_single_option",
    value: RECURRING_INSTANCE_SELECTION.SINGLE,
  },
  {
    label: "calendar_event_recurring_instance_selection_modal_edit_single_and_future_option",
    value: RECURRING_INSTANCE_SELECTION.SINGLE_AND_FUTURE,
  },
  {
    label: "calendar_event_recurring_instance_selection_modal_edit_all_option",
    value: RECURRING_INSTANCE_SELECTION.ALL,
  },
];

export const RECURRING_INSTANCE_DELETE: RecurringInstanceOption[] = [
  {
    label: "calendar_event_recurring_instance_selection_modal_delete_single_option",
    value: RECURRING_INSTANCE_SELECTION.SINGLE,
  },
  {
    label: "calendar_event_recurring_instance_selection_modal_delete_single_and_future_option",
    value: RECURRING_INSTANCE_SELECTION.SINGLE_AND_FUTURE,
  },
  {
    label: "calendar_event_recurring_instance_selection_modal_delete_all_option",
    value: RECURRING_INSTANCE_SELECTION.ALL,
  },
];

export const DAY_LIST = [
  { label: "M", value: 0, selected: false },
  { label: "T", value: 1, selected: false },
  { label: "W", value: 2, selected: false },
  { label: "T", value: 3, selected: false },
  { label: "F", value: 4, selected: false },
  { label: "S", value: 5, selected: false },
  { label: "S", value: 6, selected: false },
];

export const DAYS = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];

export const END_TYPE = {
  REPEAT_UNTIL: "repeat-until",
  REPEAT_OCCURRENCE: "repeat-occurrence",
};

export const CALENDAR_EXCEPTION_REASON = reflectKeys(["ATTACHMENT_ATTACHED", "EVENT_CHANGED"]) as {
  ATTACHMENT_ATTACHED: string;
  EVENT_CHANGED: string;
};

// \\d{2}        // Matches exactly two digits for the day (e.g., "01", "15")
// [A-Za-z]{3}   // Matches exactly three alphabetic characters for the month (e.g., "Nov", "Dec")
// \\d{4}        // Matches exactly four digits for the year (e.g., "2024")
export const CALENDAR_DATE_PATTERN = "\\d{2} [A-Za-z]{3} \\d{4}";

export const TITLE_CHARACTER_LIMIT = 250;

export const RECURRING_FREQUENCY_TYPE = {
  DAILY: "DAILY",
  WEEKLY: "WEEKLY",
  MONTHLY: "MONTHLY",
};
